import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types'; // ✅ Added
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/lib/style.css';

export default function DateRangePicker({ onRangeSelect }) {
  const [start, setStart] = useState();
  const [end, setEnd] = useState();
  const [enteredTo, setEnteredTo] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const dropdownRef = useRef(null);

  const isSelectingFirstDay = (start, end, day) => {
    const isBeforeStart = start && day < start;
    const isRangeSelected = start && end;
    return !start || isBeforeStart || isRangeSelected;
  };

  const handleDayClick = day => {
    if (start && end && day >= start && day <= end) {
      setStart(undefined);
      setEnd(undefined);
      setEnteredTo(undefined);
      return;
    }
    if (isSelectingFirstDay(start, end, day)) {
      setStart(day);
      setEnd(undefined);
      setEnteredTo(undefined);
    } else {
      setEnd(day);
      setEnteredTo(day);
      onRangeSelect({ start, end: day });
      setIsOpen(false); // Close after selection
    }
  };

  const handleDayMouseEnter = day => {
    if (!isSelectingFirstDay(start, end, day)) {
      setEnteredTo(day);
    }
  };

  const formatDate = date => {
    if (!date) return 'DD/MM/YYYY';
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div
      ref={dropdownRef}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        style={{
          padding: '8px 12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          cursor: 'pointer',
          background: '#fff',
          textAlign: 'left',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
        }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.66675 1.1665V3.49984"
            stroke="#212121"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M9.33325 1.1665V3.49984"
            stroke="#212121"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M11.0833 2.3335H2.91667C2.27233 2.3335 1.75 2.85583 1.75 3.50016V11.6668C1.75 12.3112 2.27233 12.8335 2.91667 12.8335H11.0833C11.7277 12.8335 12.25 12.3112 12.25 11.6668V3.50016C12.25 2.85583 11.7277 2.3335 11.0833 2.3335Z"
            stroke="#212121"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M1.75 5.8335H12.25"
            stroke="#212121"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        {formatDate(start)} - {formatDate(end)}
        <svg
          width="24"
          height="24"
          viewBox="0 0 14 14"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M3.5 5.25L7 8.75L10.5 5.25"
            stroke="#212121"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {start && end && (
        <button
          type="button"
          onClick={() => {
            setStart(null);
            setEnd(null);
            setEnteredTo(null);
            onRangeSelect({ start: null, end: null });
          }}
          style={{
            padding: '4px 6px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            background: '#fff',
            position: 'absolute',
            top: '5px',
            right: '5px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          title="Clear"
        >
          <svg
            width="24"
            height="24"
            viewBox="-0.18 -0.18 0.72 0.72"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMinYMin"
          >
            <path d="M.219.177.325.071A.03.03 0 1 0 .283.028L.177.135.071.028a.03.03 0 1 0-.043.043l.106.106-.106.106A.03.03 0 1 0 .07.325L.176.219l.106.106A.03.03 0 1 0 .324.283z" />
          </svg>
        </button>
      )}

      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '110%',
            zIndex: 1000,
            background: '#fff',
            border: '1px solid #ddd',
            borderRadius: 8,
            padding: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            marginTop: '8px',
            right: 0,
          }}
        >
          <DayPicker
            selectedDays={[start, { from: start, to: enteredTo }]}
            modifiers={{ start, end }}
            onDayClick={handleDayClick}
            onDayMouseEnter={handleDayMouseEnter}
            numberOfMonths={2}
            disabledDays={{ after: new Date() }}
          />
        </div>
      )}
    </div>
  );
}

// ✅ PropTypes
DateRangePicker.propTypes = {
  onRangeSelect: PropTypes.func, // a function expected
};

// ✅ Default Props
DateRangePicker.defaultProps = {
  onRangeSelect: () => {}, // safe fallback if none provided
};
